import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextareaAutosize,
  TextField
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Loading } from '../Loading';
import DialogStandard from '../DialogStandard';
import { RouterLongPing } from './RouterLongPing';

interface RouterPingAndTracerouteProps {
  cpeId: string;
  cpeName?: string;
  privateLine?: boolean;
}

type InterfaceType = 'wan0' | 'wan1' | 'cellular';

// Must stay in step with the run-ping endpoint, which sizes its command
// timeout as count * PING_INTERVAL_SEC and rejects anything outside the bounds.
const PING_COUNT_DEFAULT = 5;

export const RouterPingAndTraceroute = ({ cpeId, cpeName, privateLine }: RouterPingAndTracerouteProps) => {
  const api = useApi();
  const [result, setResult] = useState<string>('');
  const [ipaddress, setIpaddress] = useState<string>('');
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const [intf, setIntf] = useState<InterfaceType>(privateLine ? 'cellular' : 'wan0');
  const [showLongPingDialog, setShowLongPingDialog] = useState<boolean>(false);

  // The dialog can open before the CPE detail confirms the mode; only cellular works on Private Line.
  useEffect(() => {
    if (privateLine) setIntf('cellular');
  }, [privateLine]);

  const resetData = () => {
    setResult('');
    setIpaddress('');
    setButtonClicked(false);
  };

  const handleError = (e: any) => {
    resetData();
  };

  const {
    isLoading: pingLoading,
    data: pingData,
    refetch: pingRefetch,
    fetchStatus: pingStatus,
    isRefetching: pingRefetching
  } = useQuery(
    ['getPingData', cpeId, ipaddress, intf],
    () => api.runPingApiV1RunPingGet({
      node_id: cpeId,
      ip_address: ipaddress,
      interface: intf,
      count: PING_COUNT_DEFAULT,
    }),
    { enabled: false, retry: false, cacheTime: 0, staleTime: 0, initialData: undefined, onError: handleError },
  );

  const {
    isLoading: tracerouteLoading,
    data: tracerouteData,
    refetch: tracerouteRefetch,
    fetchStatus: tracerouteStatus,
    isRefetching: tracerouteRefetching
  } = useQuery(
      ['getTracerouteData', cpeId, ipaddress, intf],
      () => api.runTracerouteApiV1RunTracerouteGet({
        node_id: cpeId,
        ip_address: ipaddress,
        interface: intf,
      }),
      { enabled: false, retry: false, cacheTime: 0, staleTime: 0, initialData: undefined, onError: handleError },
  );

  const {
    isLoading: curlLoading,
    data: curlData,
    refetch: curlRefetch,
    fetchStatus: curlStatus,
    isRefetching: curlRefetching
  } = useQuery(
    ['getCurlData'],
    api.runCurlApiV1RunCurlGet.bind(null, { node_id: cpeId, url: ipaddress }),
    { enabled: false, retry: false, cacheTime: 0, staleTime: 0, initialData: undefined, onError: handleError },
  );

  const handlePing = () => {
    setButtonClicked(true);
    pingRefetch();
  }

  const handleTraceroute = () => {
    setButtonClicked(true);
    tracerouteRefetch();
  }

  const handleCurl = () => {
    setButtonClicked(true);
    curlRefetch();
  }

  useEffect(() => {
    if (pingData?.data) {
      const parsedData = (pingData.data as any).result;
      setResult(parsedData);
    }
    setButtonClicked(false);
  }, [pingData]);

  useEffect(() => {
    if (tracerouteData?.data) {
      const parsedData = (tracerouteData.data as any).result;
      setResult(parsedData);
    }
    setButtonClicked(false);
  }, [tracerouteData]);

  useEffect(() => {
    if (curlData?.data) {
      const parsedData = (curlData.data as any).result;
      setResult(parsedData);
    }
    setButtonClicked(false);
  }, [curlData]);

  const isLoading = pingLoading || tracerouteLoading || curlLoading
        || pingRefetching || tracerouteRefetching || curlRefetching;

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 5 }}>
	      Ping or Traceroute for selected device
      </Typography>
      <RadioGroup
        row
        value={intf}
        onChange={(e) => setIntf(e.target.value as InterfaceType)}
        sx={{
          mb: 6,
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 5,
        }}
      >
        {!privateLine && <>
        <FormControlLabel value="wan0" control={<Radio />} label="Primary WAN" sx={{ mx: 0 }} />
        <FormControlLabel value="wan1" control={<Radio />} label="Secondary WAN" sx={{ mx: 0 }} />
        </>}
        <FormControlLabel value="cellular" control={<Radio />} label="Cellular" sx={{ mx: 0 }} />
      </RadioGroup>
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Input the ip address or URL"
        variant="outlined"
        value={ipaddress}
        onChange={(e) => setIpaddress(e.target.value)}
        autoFocus
      />
      {(isLoading && (pingStatus !== 'idle' || tracerouteStatus !== 'idle' || curlStatus !== 'idle')) ? <Loading height={200} /> :
        <TextareaAutosize
          style={{ width: '100%' }}
          minRows={15}
          value={result}
          readOnly
        />
      }
      <Box display="flex" justifyContent="flex-end" alignItems="flex-start" sx={{ mt: 5 }}>
        <Box>
          <Button variant="outlined" onClick={handlePing} sx={{mr: 5}} disabled={isLoading && buttonClicked}>
            Ping
          </Button>
          <Button variant="outlined" onClick={() => setShowLongPingDialog(true)} sx={{mr: 5}}>
            Long Ping
          </Button>
          <Button variant="outlined" onClick={handleTraceroute} sx={{mr: 5}} disabled={isLoading && buttonClicked}>
            Traceroute
          </Button>
          <Button variant="outlined" onClick={handleCurl} disabled={isLoading && buttonClicked}>
            HTTP
          </Button>
        </Box>
      </Box>
      {showLongPingDialog && (
        <DialogStandard
          open={showLongPingDialog}
          handleClose={() => setShowLongPingDialog(false)}
          title="Long Ping"
        >
          <RouterLongPing cpeId={cpeId} cpeName={cpeName} ipAddress={ipaddress} intf={intf} />
        </DialogStandard>
      )}
    </Box>
  );
};
